const hasRedirectConfigured = (config) => !!config?.redirectSignIn;
/**
 * Cognito authentication logic to abstract and simplify the utilization of the aws-amplify library
 * @param cognito {AuthClass} @aws-amplify/auth instance
 * @param options.signin optional different signin process
 * @param options.type specify which type of token to use with auth
 * @returns {AUTH.Logic} logic to get validate and get token from cognito w/o developer needing to know the authn or authz process
 */
export const cognito = (cognito, options) => {
    const signin = options?.signin;
    const token_type = options?.type || 'id_token';
    const defaultSignIn = async (cognito) => {
        const config = cognito.configure();
        if (!hasRedirectConfigured(config?.oauth)) {
            throw 'unable to identify config is using hosted-ui';
        }
        //get parts needed to build redirect hosted-ui url
        const current = new URL(window.location.href);
        const client_id = config?.userPoolWebClientId;
        const response_type = config?.oauth?.responseType;
        const scope = config?.oauth?.scope?.join?.(' ');
        const redirect_uri = config?.oauth?.redirectSignIn;
        const redirectSignInUrl = new URL(redirect_uri, window.location.origin);
        const isLoginRedirect = current.pathname == redirectSignInUrl.pathname;
        //validate
        if (!client_id) {
            throw 'config.userPoolWebClientId missing from config';
        }
        if (!response_type) {
            throw 'config.oauth.responseType missing from config';
        }
        if (!scope) {
            throw 'config.oauth.scope missing from config';
        }
        if (!redirect_uri) {
            throw 'config.oauth.redirectSignIn missing from config';
        }
        if (isLoginRedirect) {
            throw 'current url is the redirect url';
        }
        //create url
        const login_url = '/oauth2/authorize?' + [
            `client_id=${encodeURIComponent(client_id)}`,
            `response_type=${encodeURIComponent(response_type)}`,
            `scope=${encodeURIComponent(scope)}`,
            `redirect_uri=${encodeURIComponent(redirect_uri)}`,
            `state=${encodeURIComponent(client_id)}` //replaced during build if able
        ].join('&');
        const domain = config.oauth.domain.split('://').pop();
        const url = new URL(login_url, `https://${domain}`);
        //navigate to the hosted-ui url
        window.location.replace(url);
    };
    const authz = {
        getAccessToken: async () => {
            //https://github.com/aws-amplify/amplify-js/issues/2560#issuecomment-481707972
            const session = await cognito.currentSession();
            switch (token_type) {
                case "access_token": return session.getAccessToken().getJwtToken();
                case "id_token": return session.getIdToken().getJwtToken();
                default: throw `no token for supplied token type : ${token_type}`;
            }
        }
    };
    const authn = {
        isAuthenticated: async () => {
            try {
                const session = await cognito.currentSession();
                return session.isValid();
            }
            catch {
                return false;
            }
        },
        signin: async () => {
            try {
                const cognitoSignIn = signin || defaultSignIn;
                await cognitoSignIn(cognito);
            }
            catch (error) {
                console.warn('[@growmark/axios]: issue when trying to sign-in. surpressing sign-in.', error);
            }
        }
    };
    return { authn, authz };
};
//# sourceMappingURL=index.js.map